/**
 * The panel's width, as a thing the reader controls (`V114`, `P19.4`).
 *
 * The panel was `min(28rem, 40vw)` in two rules — its own `width` and, in comparison mode,
 * `#maps`'s `right`. That is enough for a 16-mer and a health card and not enough for a residue
 * atlas's descriptor list or a wide `properties` table, and there was no way to ask for more.
 * Both rules now read one custom property and this module is the only writer of it.
 *
 * ## Why a custom property rather than styling `#panel` directly
 *
 * Two elements have to agree: the panel's own width and the map row's right inset, which is what
 * keeps a comparison pane from being drawn underneath the panel. Setting `panel.style.width`
 * would leave the second stale, and the failure — a pane whose right-hand third is hidden — looks
 * like a rendering bug rather than a layout one. One token, both rules.
 *
 * ## Why the panes are rebuilt on release and not while dragging
 *
 * deepscatter takes its canvas size once, at construction, and installs no `ResizeObserver`; a
 * pane whose element changed width keeps drawing at the old one, and its `color_pick` hit test —
 * in canvas pixels — then disagrees with what the reader sees by exactly the difference (P12.1).
 * So the caller rebuilds. **On release only**, because a rebuild re-parses tiles and a drag emits
 * a pointermove per frame.
 *
 * **In single-pane mode nothing is rebuilt at all**, and that falls out of the geometry rather
 * than being special-cased here: `#maps` is `inset: 0` with one pane in it, so the map is the
 * whole viewport at every panel width and the panel floats over it. The caller decides; this
 * module just says when a drag finished.
 */

/** The stored width, in CSS pixels. A number, so a corrupt entry is discarded rather than applied. */
const STORAGE_KEY = 'peptide-vis.panel-width';

/**
 * Bounds, in CSS pixels.
 *
 * `MIN` is about where the widest control label — "landmarks" against a select — stops wrapping.
 * `MAP_MIN` is a floor on what is left for the map rather than a ceiling on the panel, which is
 * the honest way to write it: on a narrow window the panel simply cannot take 90 % of the screen,
 * and on a wide one there is no reason to stop it.
 */
const MIN = 280;
const MAP_MIN = 240;

const clamp = (px: number): number => Math.max(MIN, Math.min(px, window.innerWidth - MAP_MIN));

function apply(px: number): void {
  document.documentElement.style.setProperty('--panel-width', `${Math.round(clamp(px))}px`);
}

/** The width the panel is actually laid out at right now, whoever decided it. */
function currentWidth(panel: HTMLElement): number {
  return panel.getBoundingClientRect().width;
}

/**
 * Make `handle` drag the panel's edge. Returns a teardown for symmetry with the rest of `panels/`.
 *
 * `onCommit` fires once per completed drag, after the width has settled — never during one.
 */
export function createPanelResizer(
  handle: HTMLElement,
  panel: HTMLElement,
  onCommit: () => void,
): () => void {
  // A width stored by a previous session, re-clamped: the window may since have been made
  // narrower, and restoring 900 px into a 600 px window would leave no map at all.
  const stored = Number(localStorage.getItem(STORAGE_KEY));
  if (Number.isFinite(stored) && stored > 0) apply(stored);

  let dragging = false;

  const onMove = (event: PointerEvent): void => {
    if (!dragging) return;
    // The panel is anchored to the right edge, so its width is the distance from the pointer to
    // that edge. Read from `clientX` rather than from a delta, so a drag that outruns the pointer
    // events cannot accumulate an offset.
    apply(window.innerWidth - event.clientX);
    event.preventDefault();
  };

  const onUp = (event: PointerEvent): void => {
    if (!dragging) return;
    dragging = false;
    handle.classList.remove('dragging');
    handle.releasePointerCapture?.(event.pointerId);
    localStorage.setItem(STORAGE_KEY, String(Math.round(currentWidth(panel))));
    onCommit();
  };

  const onDown = (event: PointerEvent): void => {
    dragging = true;
    handle.classList.add('dragging');
    // Pointer capture, so a drag that crosses the canvas keeps sending events here instead of
    // being taken over by deepscatter's own pan.
    handle.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  };

  // Double-click restores the stylesheet's own value by removing the override, which is the only
  // way back to a *responsive* width once a pixel one has been written.
  const onDoubleClick = (): void => {
    document.documentElement.style.removeProperty('--panel-width');
    localStorage.removeItem(STORAGE_KEY);
    onCommit();
  };

  // A window that shrinks below the stored width would otherwise leave no map. Re-clamping is
  // enough: the property is only rewritten when it is already a pixel value.
  const onResize = (): void => {
    if (document.documentElement.style.getPropertyValue('--panel-width') === '') return;
    apply(currentWidth(panel));
  };

  handle.addEventListener('pointerdown', onDown);
  handle.addEventListener('pointermove', onMove);
  handle.addEventListener('pointerup', onUp);
  handle.addEventListener('pointercancel', onUp);
  handle.addEventListener('dblclick', onDoubleClick);
  window.addEventListener('resize', onResize);

  return () => {
    handle.removeEventListener('pointerdown', onDown);
    handle.removeEventListener('pointermove', onMove);
    handle.removeEventListener('pointerup', onUp);
    handle.removeEventListener('pointercancel', onUp);
    handle.removeEventListener('dblclick', onDoubleClick);
    window.removeEventListener('resize', onResize);
  };
}
